import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import type { Dispatch, SetStateAction } from 'react'

import type { AvailableColumn } from '../types'
import type { ColumnCategory } from './useColumns'

export const filterEmptyColumn = (column: AvailableColumn) => column.title !== ''

export const getExitIcon = (
  columnCategories: ColumnCategory[],
  currentCategory: ColumnCategory | undefined,
) => {
  if (columnCategories.length === 1) {
    return <CloseIcon fontSize="small" />
  }

  if (currentCategory) {
    return <ArrowBackIcon fontSize="small" />
  }

  return <CloseIcon fontSize="small" />
}

export const getExitOnClick = ({
  columnCategories,
  currentCategory,
  setCurrentCategory,
  onClose,
}: {
  columnCategories: ColumnCategory[]
  currentCategory: ColumnCategory | undefined
  setCurrentCategory: Dispatch<SetStateAction<string | null>>
  onClose: () => void
}) => {
  if (columnCategories?.length === 1) {
    return onClose
  }

  if (currentCategory) {
    return () => setCurrentCategory(null)
  } else {
    return onClose
  }
}

export const isLastEnabledColumn = (
  columnIdToCheck: string,
  columnsToCheck: AvailableColumn[],
  selectedColumns: string[],
): boolean => {
  const enabledColumns = columnsToCheck.filter((column) => selectedColumns.includes(column.id))

  return enabledColumns.length === 1 && enabledColumns[0].id === columnIdToCheck
}

export const sortColumns = <Sort extends string = string>(
  columns: Array<AvailableColumn<Sort>> | undefined,
  order: string[],
) => columns?.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)) ?? null

export const areCategoriesLoaded = <Sort extends string = string>(
  categories: Array<ColumnCategory<Sort>> | undefined,
) => categories?.every((category) => Array.isArray(category.selectedNodes))

export const extractSelectedNodesFromCategories = <Sort extends string = string>(
  categories: Array<ColumnCategory<Sort>> | undefined,
) => categories?.flatMap((category) => category.selectedNodes).filter(isValidColumn)

export const isValidColumn = <Sort extends string = string>(
  column: AvailableColumn<Sort> | undefined,
): column is AvailableColumn<Sort> => !!column

export const findDynamicColumn = <Sort extends string = string>(
  categories: Array<ColumnCategory<Sort>> | undefined,
  columnId: string,
) =>
  categories
    ?.flatMap((category) => category.availableNodes)
    .find((column) => column?.id === columnId)

export const mergeSelectedColumns = <Sort extends string = string>({
  staticColumns,
  dynamicColumns,
  selectedColumns,
}: {
  staticColumns: Array<AvailableColumn<Sort>>
  dynamicColumns: Array<AvailableColumn<Sort>> | null
  selectedColumns: string[]
}): Array<AvailableColumn<Sort>> => {
  const mergedColumns = [...staticColumns, ...(dynamicColumns ?? [])]
  const empty = mergedColumns[0]?.id === 'empty' ? mergedColumns[0] : null

  // Defensive dedupe: a column list persisted before the settings merge was
  // fixed can still hold repeats, which would render the same column twice.
  const columns = Array.from(new Set(selectedColumns))
    .map((columnId) => mergedColumns.find((column) => column.id === columnId))
    .filter((i) => !!i)

  if (empty) {
    columns.unshift(empty)
  }

  return columns
}

export const rightColumnBoxShadow = '-8px 0px 8px -4px rgba(0, 0, 0, 0.12)'
